import React, { useEffect, useRef, useState } from 'react';

import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { Input } from '@wds/input';
import { Button } from '@wds/button';
import { Slide } from '@wds/motion';
import { ProgressCircle } from '@wds/progress-circle';

import { getCookie } from 'sma.common/utils';
import PropTypes from 'prop-types';

import STRINGS from '../constants';

const EmailEnrollment = (props) => {
	const {
		isLoaded,
		context,
		setContext,
		error,
		setError,
		isSliding,
		setIsSliding,
		handleChangeMethod,
		handleError,
		currentPage,
		setCurrentPage,
		isChange,
		cooldown,
		startCooldown,
	} = props;

	const [isValid, setIsValid] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [message, setMessage] = useState('');
	const [started, setStarted] = useState(false);

	useEffect(() => {
		setMessage('');
	}, [currentPage]);

	useEffect(() => {
		if (context?.status === 'code_pending') {
			setStarted(true);
			if (cooldown === 0) {
				startCooldown();
				setMessage(STRINGS.email_sent);
				setError('');
			}
		} else if (context?.status === 'code_already_pending') {
			setMessage('');
			setStarted(true);
		}
	}, [context]);

	useEffect(() => {
		if (context.challengeCode.match(/^[0-9]+$/) && context.challengeCode.length === 6) {
			setIsValid(true);
		} else {
			setIsValid(false);
		}
	}, [context.challengeCode]);

	const onHandleContextChange = (key) => (e) => {
		setContext({ ...context, [key]: e.target.value });
	};

	const startEmailEnrollment = async () => {
		try {
			setIsLoading(true);
			setMessage('');
			let url;
			let action;
			let method;
			if (isChange) {
				url = reverse('api-internal-mfa-change-device');
				action = 'begin_device_change';
				method = 'email';
			} else {
				url = reverse('api-internal-mfa-enroll');
				action = 'setup_email';
				method = 'email';
			}
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({ action, method }),
			});
			const data = await response.json();
			if (data.success) {
				setError('');
				setMessage(data.message);
				setStarted(true);
				startCooldown();
				setIsLoading(false);
			} else {
				if (data.error === 'invalid_code_locked_out' || data.error === 'not_authenticated') {
					window.location.href = reverse('mfa-lockout');
				} else if (data.error === 'code_already_pending') {
					setContext({
						...context,
						challengeCode: '',
						status: 'code_already_pending',
					});
					startCooldown(data.time_remaining);
				} else if (data.error) {
					setError(data.error);
				}
				setIsLoading(false);
			}
		} catch (err) {
			handleError(`Error starting email enrollment: ${err}`);
		} finally {
			setIsLoading(false);
		}
	};

	const resendEmailCode = async () => {
		try {
			setIsLoading(true);
			setMessage('');
			const action = 'send_code';
			const method = 'email';
			let url;
			if (isChange) {
				url = reverse('api-internal-mfa-change-device');
			} else {
				url = reverse('api-internal-mfa-enroll');
			}
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({ action, method }),
			});
			const data = await response.json();
			if (data.success) {
				setError('');
				setMessage(data.message);
				setStarted(true);
				startCooldown();
				setIsLoading(false);
			} else {
				if (data.error === 'invalid_code_locked_out' || data.error === 'not_authenticated') {
					window.location.href = reverse('mfa-lockout');
				} else if (data.error === 'code_already_pending') {
					setContext({
						...context,
						challengeCode: '',
						status: 'code_pending',
					});
					startCooldown(data.time_remaining);
				} else if (data.error) {
					setError(data.error);
				}
				setIsLoading(false);
			}
		} catch (err) {
			handleError(`Error resending email code: ${err}`);
		} finally {
			setIsLoading(false);
		}
	};

	const submitChallengeCode = async () => {
		try {
			setIsLoading(true);
			let url;
			let action;
			if (isChange) {
				url = reverse('api-internal-mfa-change-device');
				action = 'complete_device_change';
			} else {
				url = reverse('api-internal-mfa-enroll');
				action = 'verify_email_enrollment';
			}
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({
					action,
					method_id: context.method_id,
					challenge_code: context.challengeCode,
					remember_device: context.rememberDevice,
					method: 'email',
				}),
			});
			const data = await response.json();
			if (data.error) {
				if (data.error === 'not_authenticated' || data.error === 'invalid_code_locked_out') {
					window.location.href = reverse('mfa-lockout');
				} else if (data.error === 'code_already_pending') {
					setContext({
						...context,
						challengeCode: '',
						status: 'code_pending',
					});
					startCooldown(data.time_remaining);
				} else {
					setError(data.error);
				}
			}
			if (data.recovery_codes) {
				setContext((prev) => ({
					...prev,
					recovery_codes: data.recovery_codes,
				}));
				setCurrentPage('enrollment-complete');
			}
		} catch (err) {
			handleError(`Error submitting challenge code: ${err}`);
		} finally {
			setIsLoading(false);
		}
	};

	const inputColor = error ? 'error' : '';

	return (
		<Slide
			in={isLoaded && currentPage === 'email-enrollment'}
			speed={currentPage === 'email-enrollment' ? 'slow' : undefined}
			timeout={currentPage === 'email-enrollment' ? undefined : 150}
			direction='right'
			onEnter={() => setIsSliding(true)}
			onExited={() => setIsSliding(false)}
			alwaysRenderContent
		>
			<Box
				display='flex'
				flexDirection='column'
				gap={4}
				style={{ position: isSliding ? 'absolute' : 'static', width: '100%' }}
			>
				<Box
					display='flex'
					flexDirection='column'
					justifyContent='center'
					alignItems='center'
					gap={4}
				>
					<Box
						display='flex'
						flexDirection='column'
						justifyContent='center'
						alignItems='center'
						gap={4}
					>
						<Typography variant='sectionTitleSm' align='center'>
							{STRINGS.setUpMFA}
						</Typography>
						{error && (
							<Box
								p={4}
								style={{
									backgroundColor: '#AC145A',
									borderRadius: '5px',
									color: '#FFFFFF',
								}}
								role='alert'
								aria-live='assertive'
							>
								{STRINGS?.[error] || error}
							</Box>
						)}
						{isLoading && (
							<Box mb={5} role='status' aria-live='polite'>
								<ProgressCircle continuous />
							</Box>
						)}
						{message && (
							<Box
								p={4}
								style={{
									backgroundColor: '#42ac14',
									borderRadius: '5px',
									color: '#FFFFFF',
								}}
								role='alert'
								aria-live='assertive'
							>
								{STRINGS?.[message] || message}
							</Box>
						)}
						<Typography variant='bodySm' align='center' weight='medium'>
							{STRINGS.emailMFAWarning}
						</Typography>
						<Typography
							variant='link'
							component='a'
							align='center'
							href='#'
							onClick={handleChangeMethod}
							underline='hover'
						>
							{STRINGS.useAuthenticatorAppInstead}
						</Typography>
						<Box display='flex' alignItems='center' width='100%'>
							<hr
								style={{
									flex: 1,
									border: 'none',
									borderTop: '1px solid #cccccc80',
									margin: 0,
								}}
							/>
							<Box px={2}>
								<Typography
									variant='caption'
									style={{ textTransform: 'uppercase' }}
								>
									{STRINGS.or}
								</Typography>
							</Box>
							<hr
								style={{
									flex: 1,
									border: 'none',
									borderTop: '1px solid #cccccc80',
									margin: 0,
								}}
							/>
						</Box>
						{started || cooldown ? (
							<Button
								onClick={() => resendEmailCode()}
								id='start-resend-email-button'
								size='lg'
								aria-label={STRINGS.resendCode}
								disabled={isLoading || cooldown > 0}
							>
								{cooldown > 0 ? `${STRINGS.resendCode} (${cooldown})` : (STRINGS.resendCode)}
							</Button>
						) : (
							<Button
								onClick={() => startEmailEnrollment()}
								id='start-email-enrollment-button'
								size='lg'
								aria-label={STRINGS.startEmailEnrollment}
								disabled={isLoading || cooldown > 0}
							>
								{STRINGS.startEmailEnrollment}
							</Button>
						)}
					</Box>
				</Box>
				<Box display='flex' alignItems='center' width='100%'>
					<hr
						style={{
							flex: 1,
							border: 'none',
							borderTop: '1px solid #cccccc80',
							margin: 0,
						}}
					/>
					<Box px={2}>
						<Typography
							variant='caption'
							style={{ textTransform: 'uppercase' }}
						>
							{STRINGS.then}
						</Typography>
					</Box>
					<hr
						style={{
							flex: 1,
							border: 'none',
							borderTop: '1px solid #cccccc80',
							margin: 0,
						}}
					/>
				</Box>
				<Box
					display='flex'
					flexDirection='column'
					gap={4}
					pb={6}
				>
					<Input
						value={context.challengeCode}
						placeholder={STRINGS.enterCodeProvided}
						onChange={onHandleContextChange('challengeCode')}
						id='mfa-challenge-code'
						type='text'
						color={inputColor}
						stretched
						aria-required='true'
						autoFocus
					/>
					<Button
						id='next-button'
						size='lg'
						disabled={isLoading || !isValid}
						aria-disabled={!isValid}
						onClick={submitChallengeCode}
					>
						{STRINGS.continue}
					</Button>
					<Typography
						variant='link'
						component='a'
						href='#'
						align='center'
						onClick={handleChangeMethod}
					>
						{STRINGS.tryAnotherMethod}
					</Typography>
				</Box>
			</Box>
		</Slide>
	);
};

EmailEnrollment.propTypes = {
	isLoaded: PropTypes.bool.isRequired,
	context: PropTypes.object.isRequired,
	setContext: PropTypes.func.isRequired,
	error: PropTypes.string,
	isSliding: PropTypes.bool.isRequired,
	setIsSliding: PropTypes.func.isRequired,
	handleChangeMethod: PropTypes.func.isRequired,
	handleError: PropTypes.func.isRequired,
	currentPage: PropTypes.string.isRequired,
	setCurrentPage: PropTypes.func.isRequired,
	isChange: PropTypes.bool,
	cooldown: PropTypes.number.isRequired,
	startCooldown: PropTypes.func.isRequired,
};

EmailEnrollment.defaultProps = { error: '', isChange: false };

export default EmailEnrollment;
